import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from './utils/experienceStore';
import {
    reorderProcessorNodes,
    selectProcessorNode,
    removeProcessorNode,
    redrawFlowLayout,
} from './utils/state/processingFlowSlice';
import {
    ChevronDown,
    ChevronRight,
    Settings,
    Trash2,
    ArrowUp,
    ArrowDown,
    Zap,
    RotateCcw,
} from 'lucide-react';

interface FlowSequencePanelProps {
    width?: number;
}

export default function FlowSequencePanel({
    width = 280,
}: FlowSequencePanelProps) {
    const dispatch = useDispatch();
    const [isExpanded, setIsExpanded] = useState(true);
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

    const { nodes, selectedNodeId } = useSelector(
        (state: RootState) => state.flows.present,
    );

    const handleNodeSelect = (nodeId: string) => {
        dispatch(selectProcessorNode(nodeId));
    };

    const handleNodeDelete = (nodeId: string) => {
        if (window.confirm('Are you sure you want to delete this processor?')) {
            dispatch(removeProcessorNode(nodeId));
        }
    };

    const handleMoveUp = (index: number) => {
        if (index > 0) {
            dispatch(
                reorderProcessorNodes({
                    fromIndex: index,
                    toIndex: index - 1,
                }),
            );
        }
    };

    const handleMoveDown = (index: number) => {
        if (index < nodes.length - 1) {
            dispatch(
                reorderProcessorNodes({
                    fromIndex: index,
                    toIndex: index + 1,
                }),
            );
        }
    };

    const handleDragStart = (e: React.DragEvent, index: number) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', index.toString());
    };

    const handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverIndex(index);
    };

    const handleDragLeave = () => {
        setDragOverIndex(null);
    };

    const handleDrop = (e: React.DragEvent, dropIndex: number) => {
        e.preventDefault();

        if (draggedIndex !== null && draggedIndex !== dropIndex) {
            dispatch(
                reorderProcessorNodes({
                    fromIndex: draggedIndex,
                    toIndex: dropIndex,
                }),
            );
        }

        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const getNodeIcon = (processorName: string) => {
        const name = processorName.toLowerCase();
        if (name.includes('resize') || name.includes('crop')) return '✂️';
        if (name.includes('filter') || name.includes('blur')) return '🎨';
        if (name.includes('overlay') || name.includes('composite')) return '🔗';
        if (name.includes('color') || name.includes('brightness')) return '🌈';
        if (name.includes('text') || name.includes('watermark')) return '📝';
        return '⚡';
    };

    return (
        <div
            className="bg-background border-r border-border flex flex-col h-full overflow-hidden"
            style={{ width }}
        >
            {/* Header */}
            <div className="p-4 border-b border-border bg-muted/50">
                <div className="flex items-center justify-between">
                    <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-foreground"
                    >
                        {isExpanded ? (
                            <ChevronDown className="w-4 h-4" />
                        ) : (
                            <ChevronRight className="w-4 h-4" />
                        )}
                        <Zap className="w-4 h-4" />
                        <span>Flow Sequence</span>
                    </button>
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={() => dispatch(redrawFlowLayout())}
                            className="p-1 text-muted-foreground hover:text-muted-foreground rounded transition-colors"
                            title="Redraw flow in sequential order"
                        >
                            <RotateCcw className="w-4 h-4" />
                        </button>
                        <span className="text-xs text-muted-foreground">
                            {nodes.length} steps
                        </span>
                    </div>
                </div>
            </div>

            {/* Flow Steps */}
            {isExpanded && (
                <div className="flex-1 overflow-y-auto p-2">
                    {nodes.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                            <Zap className="w-8 h-8 mx-auto mb-2 text-muted-foreground/50" />
                            <p className="text-sm">No processors in flow</p>
                            <p className="text-xs mt-1">
                                Drag processors from the palette to build your
                                flow
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {nodes.map((node, index) => {
                                const isSelected = selectedNodeId === node.id;
                                const nodeIcon = getNodeIcon(
                                    node.processor.name,
                                );

                                return (
                                    <div key={node.id} className="relative">
                                        {/* Connection line to next node */}
                                        {index < nodes.length - 1 && (
                                            <div className="absolute left-6 top-12 w-0.5 h-6 bg-muted" />
                                        )}

                                        <div
                                            className={`relative rounded-lg border-2 transition-all cursor-move ${
                                                isSelected
                                                    ? 'border-blue-500 bg-blue-50'
                                                    : dragOverIndex === index
                                                    ? 'border-green-400 bg-green-50'
                                                    : draggedIndex === index
                                                    ? 'border-gray-400 bg-muted opacity-50'
                                                    : 'border-border bg-background hover:border-border hover:bg-muted/50'
                                            }`}
                                            draggable
                                            onDragStart={(e) =>
                                                handleDragStart(e, index)
                                            }
                                            onDragOver={(e) =>
                                                handleDragOver(e, index)
                                            }
                                            onDragLeave={handleDragLeave}
                                            onDrop={(e) => handleDrop(e, index)}
                                            onDragEnd={handleDragEnd}
                                            onClick={() =>
                                                handleNodeSelect(node.id)
                                            }
                                        >
                                            {/* Step number indicator */}
                                            <div className="absolute -left-2 top-3 w-6 h-6 bg-background border-2 border-border rounded-full flex items-center justify-center text-xs font-medium text-muted-foreground">
                                                {index + 1}
                                            </div>

                                            <div className="p-3 pl-6">
                                                <div className="flex items-start justify-between">
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center space-x-2 mb-1">
                                                            <span className="text-lg">
                                                                {nodeIcon}
                                                            </span>
                                                            <h4 className="text-sm font-medium text-foreground truncate">
                                                                {node.processor
                                                                    .title ||
                                                                    node
                                                                        .processor
                                                                        .name}
                                                            </h4>
                                                        </div>

                                                        {node.processor
                                                            .description && (
                                                            <p className="text-xs text-muted-foreground mb-2 line-clamp-2">
                                                                {
                                                                    node
                                                                        .processor
                                                                        .description
                                                                }
                                                            </p>
                                                        )}

                                                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                                                            <span>
                                                                {
                                                                    Object.keys(
                                                                        node
                                                                            .processor
                                                                            .arguments,
                                                                    ).filter(
                                                                        (key) =>
                                                                            key !==
                                                                                'in' &&
                                                                            key !==
                                                                                'out',
                                                                    ).length
                                                                }{' '}
                                                                args
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <div className="flex flex-col items-center space-y-1 ml-2">
                                                        {/* Reorder buttons */}
                                                        <div className="flex flex-col space-y-1">
                                                            <button
                                                                onClick={(
                                                                    e,
                                                                ) => {
                                                                    e.stopPropagation();
                                                                    handleMoveUp(
                                                                        index,
                                                                    );
                                                                }}
                                                                disabled={
                                                                    index === 0
                                                                }
                                                                className="p-1 text-muted-foreground hover:text-muted-foreground rounded disabled:opacity-30 disabled:cursor-not-allowed"
                                                                title="Move up"
                                                            >
                                                                <ArrowUp className="w-3 h-3" />
                                                            </button>
                                                            <button
                                                                onClick={(
                                                                    e,
                                                                ) => {
                                                                    e.stopPropagation();
                                                                    handleMoveDown(
                                                                        index,
                                                                    );
                                                                }}
                                                                disabled={
                                                                    index ===
                                                                    nodes.length -
                                                                        1
                                                                }
                                                                className="p-1 text-muted-foreground hover:text-muted-foreground rounded disabled:opacity-30 disabled:cursor-not-allowed"
                                                                title="Move down"
                                                            >
                                                                <ArrowDown className="w-3 h-3" />
                                                            </button>
                                                        </div>

                                                        {/* Action buttons */}
                                                        <div className="flex space-x-1">
                                                            <button
                                                                onClick={(
                                                                    e,
                                                                ) => {
                                                                    e.stopPropagation();
                                                                    handleNodeSelect(
                                                                        node.id,
                                                                    );
                                                                }}
                                                                className="p-1 text-muted-foreground hover:text-muted-foreground rounded"
                                                                title="Edit processor"
                                                            >
                                                                <Settings className="w-3 h-3" />
                                                            </button>
                                                            <button
                                                                onClick={(
                                                                    e,
                                                                ) => {
                                                                    e.stopPropagation();
                                                                    handleNodeDelete(
                                                                        node.id,
                                                                    );
                                                                }}
                                                                className="p-1 text-muted-foreground hover:text-red-600 rounded"
                                                                title="Delete processor"
                                                            >
                                                                <Trash2 className="w-3 h-3" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* Footer */}
            <div className="p-3 border-t border-border bg-muted/50">
                <div className="text-xs text-muted-foreground text-center">
                    Sequential processing flow • {nodes.length} processors
                </div>
            </div>
        </div>
    );
}
